import React from 'react';
import { PilotData } from '../../types';

interface PilotCardProps {
  pilot: PilotData;
  isSelected: boolean;
  isFollowing: boolean;
  onSelect: (pilotId: string | null) => void;
  onCenter: (pilot: PilotData) => void;
  onFollow: (pilotId: string | null) => void;
}

const PilotCard: React.FC<PilotCardProps> = ({
  pilot,
  isSelected,
  isFollowing,
  onSelect,
  onCenter,
  onFollow,
}) => {
  // Calculate time since last update
  const now = Date.now();
  const lastUpdate = pilot.timestamp || now;
  const timeDiff = Math.floor((now - lastUpdate) / 1000);
  let timeString = 'just now';

  if (timeDiff < 10) {
    timeString = 'just now';
  } else if (timeDiff < 60) {
    timeString = `${timeDiff}s ago`;
  } else if (timeDiff < 3600) {
    const minutes = Math.floor(timeDiff / 60);
    timeString = `${minutes}m ago`;
  } else if (timeDiff < 86400) {
    const hours = Math.floor(timeDiff / 3600);
    const minutes = Math.floor((timeDiff % 3600) / 60);
    timeString = minutes > 0 ? `${hours}h ${minutes}m ago` : `${hours}h ago`;
  } else {
    const days = Math.floor(timeDiff / 86400);
    timeString = days === 1 ? '1 day ago' : `${days} days ago`;
  }

  // Calculate flight time
  const flightTime = pilot.flightStateInfo?.flight_time || pilot.flightTime || 0;
  const hours = Math.floor(flightTime / 3600);
  const minutes = Math.floor((flightTime % 3600) / 60);
  const flightTimeString = flightTime > 0 ? (hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`) : '0m';

  return (
    <div
      className={`pilot-card ${isSelected ? 'selected' : ''} ${isFollowing ? 'following' : ''}`}
      onClick={() => onSelect(isSelected ? null : pilot.id)}
    >
      <div className="pilot-card-header">
        <span
          className="pilot-color-indicator"
          style={{ backgroundColor: pilot.color }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span className="pilot-name" style={{ flexShrink: 0 }}>{pilot.name}</span>
            {pilot.flightState && (
              <span
                className={`flight-state-tag ${pilot.flightState.toLowerCase()}`}
                style={{
                  fontSize: '9px',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  opacity: 0.8
                }}
              >
                {pilot.flightState.toUpperCase()}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span className="pilot-altitude">{Math.round(pilot.altitude)}m</span>
          <span style={{ fontSize: '10px', opacity: 0.6, fontWeight: 400 }}>{timeString}</span>
        </div>
      </div>

      {isSelected && (
        <div className="pilot-card-details">
          {/* Primary flight metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <div className="pilot-stat">
              <span className="stat-label">Speed:</span>
              <span className="stat-value">{pilot.speed.toFixed(1)} km/h</span>
            </div>
            <div className="pilot-stat">
              <span className="stat-label">Vario:</span>
              <span className={`stat-value ${pilot.vario > 0 ? 'positive' : 'negative'}`}>
                {pilot.vario > 0 ? '+' : ''}{pilot.vario.toFixed(1)} m/s
              </span>
            </div>
            <div className="pilot-stat">
              <span className="stat-label">Heading:</span>
              <span className="stat-value">{Math.round(pilot.heading)}°</span>
            </div>
            <div className="pilot-stat">
              <span className="stat-label">Flight Time:</span>
              <span className="stat-value">{flightTimeString}</span>
            </div>
          </div>

          {/* Performance summary */}
          {(pilot.maxSpeed || pilot.altitudeChange !== undefined || pilot.avgSpeed) && (
            <div style={{
              fontSize: '12px',
              marginBottom: '12px',
              padding: '8px',
              background: 'linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(59,130,246,0.05) 100%)',
              borderRadius: '6px',
              border: '1px solid rgba(99,102,241,0.1)'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '6px', color: '#4F46E5' }}>Performance</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                {pilot.maxSpeed && (
                  <div>Max: {pilot.maxSpeed.toFixed(1)} km/h</div>
                )}
                {pilot.avgSpeed && (
                  <div>Avg: {pilot.avgSpeed.toFixed(1)} km/h</div>
                )}
                {pilot.altitudeChange !== undefined && (
                  <div>Alt Δ: {pilot.altitudeChange > 0 ? '+' : ''}{pilot.altitudeChange.toFixed(0)}m</div>
                )}
              </div>
            </div>
          )}

          {/* Metadata section */}
          {(pilot.source || pilot.confidence || pilot.totalPoints || pilot.lastFixTime) && (
            <div style={{
              fontSize: '11px',
              marginBottom: '12px',
              padding: '6px',
              background: 'rgba(0,0,0,0.03)',
              borderRadius: '4px',
              color: '#6B7280'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>Tracking Info</div>
              {pilot.source && (
                <div>Source: {pilot.source}</div>
              )}
              {pilot.confidence && (
                <div>Confidence: {pilot.confidence}</div>
              )}
              {pilot.totalPoints && (
                <div>Track points: {pilot.totalPoints}</div>
              )}
              {pilot.lastFixTime && (
                <div>Last fix: {new Date(pilot.lastFixTime).toLocaleTimeString()}</div>
              )}
            </div>
          )}

          <div className="pilot-actions">
            <button
              className="pilot-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onCenter(pilot);
              }}
            >
              Center
            </button>
            <button
              className={`pilot-action-btn ${isFollowing ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onFollow(isFollowing ? null : pilot.id);
              }}
            >
              {isFollowing ? 'Unfollow' : 'Follow'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotCard;